/**
 * SVG sanitizer for model-authored diagrams.
 *
 * This is a security boundary, not a formatting pass. The input is untrusted —
 * treat it exactly like user-submitted content — and the output goes into the
 * DOM via dangerouslySetInnerHTML, so anything that survives here executes with
 * our origin's privileges.
 *
 * Strategy is a strict ALLOWLIST, rebuilt node by node into a fresh document.
 * A denylist loses to the next attribute someone thinks of; an allowlist fails
 * closed. Anything not named below is dropped.
 *
 * Parsing uses DOMParser with image/svg+xml, which builds an inert tree —
 * scripts do not run and external references are not fetched at parse time.
 * Nothing is ever assigned to innerHTML before it has been through here.
 *
 * Returns { ok: true, svg } or { ok: false, reason }. Caps REJECT rather than
 * truncate: half a diagram is not a diagram, and silently dropping nodes hides
 * the fact that the model overran its budget.
 */

const ALLOWED_ELEMENTS = new Set([
  "svg", "g", "rect", "circle", "ellipse", "line", "polyline", "polygon",
  "path", "text", "tspan", "defs", "marker", "clippath", "title", "desc"
]);

// Geometry, presentation and accessibility only. No href of any kind: internal
// references travel through url(#id), which is validated separately.
const ALLOWED_ATTRS = new Set([
  // structure / geometry
  "viewbox", "width", "height", "x", "y", "x1", "y1", "x2", "y2", "cx", "cy",
  "r", "rx", "ry", "d", "points", "dx", "dy", "transform", "preserveaspectratio",
  // presentation
  "fill", "fill-opacity", "fill-rule", "stroke", "stroke-width", "stroke-opacity",
  "stroke-linecap", "stroke-linejoin", "stroke-dasharray", "stroke-dashoffset",
  "opacity", "color", "vector-effect", "shape-rendering",
  // text
  "font-size", "font-weight", "font-family", "font-style", "text-anchor",
  "dominant-baseline", "alignment-baseline", "letter-spacing", "xml:space",
  // defs / markers / clipping
  "id", "marker-start", "marker-mid", "marker-end", "markerwidth", "markerheight",
  "refx", "refy", "orient", "markerunits", "clip-path", "clippathunits",
  // accessibility
  "role", "aria-label", "aria-labelledby", "aria-hidden",
  // host interaction — value is read as TEXT and sent as a chat message
  "data-prompt",
  // scrubbed, not trusted
  "style", "class"
]);

// Attributes whose value may legitimately be a url(...) reference.
const URL_REF_ATTRS = new Set([
  "fill", "stroke", "clip-path", "marker-start", "marker-mid", "marker-end"
]);

const MAX_BYTES = 200000;
const MAX_NODES = 1500;

/** Only `url(#same-document-id)` is acceptable. Anything else is external. */
const urlRefIsSafe = value => {
  const refs = String(value).match(/url\(([^)]*)\)/gi);
  if (!refs) return true;
  return refs.every(ref => /^url\(\s*['"]?#[A-Za-z0-9_.:-]+['"]?\s*\)$/.test(ref));
};

const hasDangerousScheme = value =>
  /(?:javascript|data|vbscript)\s*:/i.test(String(value).replace(/\s+/g, ""));

/**
 * Style attributes are kept because diagrams lean on them, but scrubbed:
 * url() can fetch cross-origin and scheme tricks can execute.
 */
const sanitizeStyle = value => {
  const raw = String(value);
  if (/url\s*\(/i.test(raw) || hasDangerousScheme(raw) || /expression\s*\(/i.test(raw)) {
    return null;
  }
  return raw;
};

/**
 * data-prompt is the ONE free-text attribute we allow, and its value is sent
 * to the chat verbatim. Do not rely on the serializer to escape it: browsers'
 * XMLSerializer escapes `<` and `"` inside attribute values, jsdom's outerHTML
 * does not, and this string later goes through an HTML parse via
 * dangerouslySetInnerHTML. Strip the characters that could terminate the
 * attribute or open a tag, so the value cannot become markup in ANY parser.
 * It is a prompt sentence — losing angle brackets and quotes costs nothing.
 */
const PROMPT_MAX = 240;
const sanitizePromptValue = value =>
  String(value)
    .replace(/[<>"'&]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, PROMPT_MAX);

const localName = node => (node.localName || node.nodeName || "").toLowerCase();

/**
 * Browsers have XMLSerializer; the jsdom bundled with this CRA does not, but
 * exposes outerHTML on XML elements. Both escape attribute values and text on
 * output, which is what the sanitizer relies on — so either is safe.
 */
const serializeNode = node => {
  if (typeof XMLSerializer !== "undefined") {
    return new XMLSerializer().serializeToString(node);
  }
  if (typeof window !== "undefined" && window.XMLSerializer) {
    return new window.XMLSerializer().serializeToString(node);
  }
  return node.outerHTML || "";
};

export const sanitizeSvg = input => {
  if (typeof input !== "string" || !input.trim()) {
    return { ok: false, reason: "Empty or non-string SVG." };
  }
  if (input.length > MAX_BYTES) {
    return { ok: false, reason: `SVG too large (${input.length} > ${MAX_BYTES} bytes).` };
  }

  let parsed;
  try {
    parsed = new DOMParser().parseFromString(input, "image/svg+xml");
  } catch (e) {
    return { ok: false, reason: "SVG could not be parsed." };
  }

  // DOMParser reports XML errors as a <parsererror> node rather than throwing.
  if (parsed.getElementsByTagName("parsererror").length) {
    return { ok: false, reason: "SVG is malformed." };
  }

  const root = parsed.documentElement;
  if (!root || localName(root) !== "svg") {
    return { ok: false, reason: "Root element must be <svg>." };
  }

  let nodeCount = 0;
  let overflowed = false;

  const SVG_NS = "http://www.w3.org/2000/svg";
  const out = parsed.implementation.createDocument(SVG_NS, "svg", null);

  const copyAttributes = (source, target) => {
    Array.from(source.attributes || []).forEach(attr => {
      // Lowercase for the allowlist lookup only. SVG is XML and its attribute
      // names are CASE-SENSITIVE (viewBox, markerWidth, refX,
      // preserveAspectRatio), so the original spelling is what gets written
      // back — lowercasing it silently produces an attribute the renderer
      // ignores.
      const name = attr.name.toLowerCase();
      const originalName = attr.name;
      const value = attr.value;

      // Belt and braces: on* can never be allowlisted, but check anyway so a
      // future edit to ALLOWED_ATTRS cannot open this up by accident.
      if (name.startsWith("on")) return;
      if (name === "href" || name === "xlink:href" || name.endsWith(":href")) return;
      if (!ALLOWED_ATTRS.has(name)) return;

      if (name === "style") {
        const safe = sanitizeStyle(value);
        if (safe !== null) target.setAttribute(originalName, safe);
        return;
      }

      if (name === "data-prompt") {
        const prompt = sanitizePromptValue(value);
        if (prompt) target.setAttribute("data-prompt", prompt);
        return;
      }

      if (hasDangerousScheme(value)) return;
      if (URL_REF_ATTRS.has(name) && !urlRefIsSafe(value)) return;

      // setAttribute escapes the value on serialization, so a data-prompt
      // carrying markup becomes text, never nodes.
      target.setAttribute(originalName, value);
    });
  };

  const walk = (source, target) => {
    Array.from(source.childNodes).forEach(child => {
      if (overflowed) return;

      if (child.nodeType === 3) {
        // Text node — createTextNode escapes on serialization.
        target.appendChild(out.createTextNode(child.nodeValue));
        return;
      }
      if (child.nodeType !== 1) return; // drop comments, CDATA, PIs

      const tag = localName(child);
      // Disallowed element: drop it AND its subtree. Hoisting children of a
      // <foreignObject> would defeat the point of removing it.
      if (!ALLOWED_ELEMENTS.has(tag)) return;

      nodeCount += 1;
      if (nodeCount > MAX_NODES) {
        overflowed = true;
        return;
      }

      const clone = out.createElementNS(SVG_NS, child.localName || tag);
      copyAttributes(child, clone);
      target.appendChild(clone);
      walk(child, clone);
    });
  };

  const newRoot = out.documentElement;
  copyAttributes(root, newRoot);
  walk(root, newRoot);

  if (overflowed) {
    return { ok: false, reason: `SVG has too many nodes (over ${MAX_NODES}).` };
  }

  // A viewBox is what lets the diagram scale to the chat column. Without one a
  // fixed width/height would overflow, so synthesize from the declared size.
  if (!newRoot.getAttribute("viewBox")) {
    const width = parseFloat(newRoot.getAttribute("width"));
    const height = parseFloat(newRoot.getAttribute("height"));
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      newRoot.setAttribute("viewBox", `0 0 ${width} ${height}`);
    }
  }
  // Let CSS own the box; intrinsic px sizing fights the container.
  newRoot.removeAttribute("width");
  newRoot.removeAttribute("height");

  // Report the authored width so the host can avoid UPSCALING. A diagram is
  // authored in its own coordinate space: stretching a 200-wide viewBox to fill
  // a 900px column multiplies every font-size by 4.5, so identical
  // `font-size="12"` renders tiny in one diagram and huge in the next. Cap at
  // the intrinsic width and text stays consistent across diagrams.
  const viewBox = (newRoot.getAttribute("viewBox") || "").trim().split(/[\s,]+/).map(Number);
  const intrinsicWidth =
    viewBox.length === 4 && Number.isFinite(viewBox[2]) && viewBox[2] > 0 ? viewBox[2] : null;

  const serialized = serializeNode(newRoot);
  return { ok: true, svg: serialized, intrinsicWidth };
};

export default sanitizeSvg;
